import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Box, Toolbar, useMediaQuery } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState, useCallback } from 'react'
import { jwtDecode } from 'jwt-decode'

import sizeConfigs from '../../configs/SizeConfigs'
import Sidebar from '../common/Sidebar'
import Topbar from '../common/Topbar'
import { checkAuthStatus, setAuthentication } from '../../redux/features/authSlice'
import {
  LoadingSpinner,
  LoadingSpinnerWrapper,
} from '../../style/StyledElement'
import { apiConfig } from '../../api/ApiConfig'

const { logUserEvent } = apiConfig()

const MainLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { userId, isAuthenticated, isTokenAuth } = useSelector((state) => state.auth)
  
  const [authChecked, setAuthChecked] = useState(false)
  const isSmallScreen = useMediaQuery('(max-width:768px)')
  const [sidebarVisible, setSidebarVisible] = useState(!isSmallScreen)

  // Memoized navigation function
  const navigateToLogin = useCallback(() => {
    navigate('/login')
  }, [navigate])

  // Memoized sidebar toggle
  const toggleSidebar = useCallback(() => {
    setSidebarVisible(prev => !prev)
  }, [])

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      await dispatch(checkAuthStatus())
      setAuthChecked(true)
    }
    checkAuth()
  }, [dispatch])

  // Handle authentication state changes with token support
  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      const token = searchParams.get('token')
      let validTokenAccess = false

      if (token) {
        const allowedPaths = ['/admet', '/SolPred', '/Drugex', '/docs']
        const currentPath = location.pathname.toLowerCase()

        const isAllowedPath = allowedPaths.some(path =>
          currentPath === path.toLowerCase() ||
          currentPath.startsWith(path.toLowerCase() + '/')
        )

        if (isAllowedPath) {
          try {
            const decoded = jwtDecode(token)
            const currentTime = Date.now() / 1000
            if (decoded.exp && decoded.exp > currentTime) {
              validTokenAccess = true
              dispatch(setAuthentication({
                isAuthenticated: true,
                token: token,
                userId: decoded.sub,
                isTokenAuth: true
              }))
            }
          } catch (error) {
            console.error('Invalid token:', error)
          }
        }
      }

      if (!validTokenAccess) {
        navigateToLogin()
      }
    }
  }, [authChecked, isAuthenticated, navigateToLogin, searchParams, location.pathname, dispatch])

  // Update sidebar visibility based on screen size
  useEffect(() => {
    setSidebarVisible(!isSmallScreen)
  }, [isSmallScreen])

  // Track page views
  useEffect(() => {
    if (isAuthenticated && userId) {
      logUserEvent({
        userId,
        eventType: 'PAGE_VIEW',
        eventName: location.pathname,
        metadata: {
          token_auth: isTokenAuth || false
        },
        timestamp: new Date().toISOString()
      })
    }
  }, [location.pathname, isAuthenticated, userId, isTokenAuth])

  // Show loading spinner while checking authentication
  if (!authChecked) {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner />
      </LoadingSpinnerWrapper>
    )
  }

  // Calculate main content width dynamically
  const mainContentWidth = isSmallScreen 
    ? '100%' 
    : `calc(100% - ${sizeConfigs.sidebar.width})`

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Topbar 
        username={userId} 
        toggleSidebar={toggleSidebar} 
        text="" 
      />
      
      <Box
        component="nav"
        sx={{
          width: isSmallScreen ? 0 : sizeConfigs.sidebar.width,
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {(sidebarVisible || !isSmallScreen) && <Sidebar />}
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: mainContentWidth,
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  )
}

export default MainLayout
